<template>
  <div class="contractDetailsForm">
    <div>
      <h3 class="card-title font-weight-bold">
        Version {{ versionId }}
      </h3>

      <b-tabs content-class="mt-3 p-3">
        <!-- Tab Version-Details -->
        <b-tab title="Version-Details">
          <version-form
            :contract-id="contractId"
            :selected-languages="selectedLanguages"
            :version-id="versionId"
            :version-id-id="versionIdId"
            @VersionDeleted="
              (versionId) => {
                $emit('VersionDeleted', versionId);
              }
            "
            @translatableLanguagesChanged="handleLangChange($event)"
          />
        </b-tab>

        <!-- Tab Tenants -->
        <b-tab title="Tenants">
          <version-tenants
            :can-user-edit="
              $IsUserInRole('VertragsmanagementVerwaltung') ||
                $IsUserInRole('VertragsmanagementUniaVertragsverantworlich')
            "
            :contract-id="contractId"
            :selected-languages="selectedLanguages"
            :version-id="versionId"
            :version-id-id="versionIdId"
          />
        </b-tab>
      </b-tabs>

      <template v-if="showEdition">
        <edition-buttons
          :key="currentEditionId"
          :contract-id="contractId"
          :edition-id="currentEditionId"
          :selected-section="mode"
          :version-id="versionId"
          @modeChanged="editionModeChanged"
        />

        <h3 class="card-title font-weight-bold editionSection pt-4">
          Edition {{ currentEditionId }}
        </h3>

        <b-alert
          v-if="
            !canUserEdit &&
              editionState == 3 &&
              $IsUserInRole('VertragsmanagementVerwaltung')
          "
          show
          variant="info"
        >
          Diese Edition ist aktualisiert. Bitte erstellen Sie eine neue Edition,
          um Anpassungen vorzunehmen.
        </b-alert>

        <b-alert
          v-if="
            !canUserEdit &&
              editionState != 3 &&
              $IsUserInRole('VertragsmanagementVerwaltung')
          "
          show
          variant="info"
        >
          Sie haben diese Edition nicht erstellt oder die Edition ist nicht im
          Status Edit und kann daher nicht bearbeitet werden. Um die Version zu
          bearbeiten, erstellen Sie bitte eine neue Edition.
        </b-alert>

        <edition-overview
          v-if="mode == editionEditModes.editDetails"
          :can-user-edit="canUserEdit"
          :contract-id="contractId"
          :version-id="versionId"
          :version-id-id="versionIdId"
          @currentEditionLoaded="currentEditionLoaded"
        />

        <edition-cantons
          v-if="mode == editionEditModes.editCantons"
          :can-user-edit="canUserEdit"
          :contract-id="contractId"
          :edition-id="currentEditionId"
          :version-id="versionId"
        />

        <edition-noga-branches
          v-if="mode == editionEditModes.editNogaBranches"
          :can-user-edit="canUserEdit"
          :contract-id="contractId"
          :edition-id="currentEditionId"
          :version-id="versionId"
        />

        <edition-links
          v-if="mode == editionEditModes.editLinks"
          :can-user-edit="canUserEdit"
          :contract-id="contractId"
          :edition-id="currentEditionId"
          :version-id="versionId"
        />

        <edition-attachments
          v-if="mode == editionEditModes.editAttatchments"
          :can-user-edit="canUserEdit"
          :contract-id="contractId"
          :edition-id="currentEditionId"
          :version-id="versionId"
        />

        <edition-criterion-management
          v-if="mode == editionEditModes.editCriterions"
          :can-user-edit="canUserEdit"
          :contract-id="contractId"
          :edition-id="currentEditionId"
          :version-id="versionId"
        />
        <edition-minimum-salary
          v-if="mode == editionEditModes.minimumWageConfigurations"
          :can-user-edit="canUserEdit"
          :contract-id="contractId"
          :edition-id="currentEditionId"
          :version-id="versionId"
          :show-wage-supplement="currentEdition.supplementCalculatorVisible"
        />

        <edition-translations
          v-if="mode == editionEditModes.editLanguages"
          :can-user-edit="canUserEdit"
          :contract-id="contractId"
          :edition-id="currentEditionId"
          :version-id="versionId"
        />
      </template>
    </div>
  </div>
</template>

<script>
import EditionOverview from "@/components/contract/edition/EditionOverview.vue";
import VersionForm from "@/components/contract/version/VersionForm.vue";
import EditionButtons from "@/components/contract/edition/EditionButtons.vue";
import EditionNogaBranches from "@/components/contract/edition/EditionNogaBranches.vue";
import VersionTenants from "@/components/contract/version/VersionTenants.vue";
import EditionCantons from "@/components/contract/edition/EditionCantons.vue";
import EditionLinks from "@/components/contract/edition/EditionLinks.vue";
import EditionAttachments from "@/components/contract/edition/EditionAttachments.vue";
import EditionCriterionManagement from "@/components/contract/edition/Criterion/EditionCriterionManagement.vue";
import { UniaGAVConstants } from "@/assets/configuration/constants.js";
import { MultiLangMixin } from "@/mixins/MultilanguageContentsMixin";
import EditionMinimumSalary from "../edition/EditionMinimumSalary.vue";
import EditionTranslations from "@/components/contract/edition/EditionTranslations.vue";

export default {
  components: {
    EditionOverview,
    EditionButtons,
    VersionForm,
    EditionLinks,
    EditionAttachments,
    EditionCriterionManagement,
    EditionNogaBranches,
    VersionTenants,
    EditionCantons,
    EditionMinimumSalary,
    EditionTranslations,
  },
  mixins: [MultiLangMixin],
  props: {
    contractId: {
      type: String,
      default: "",
    },
    versionId: {
      type: Number,
      default: 0,
    },
    versionIdId: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      showEdition: true,
      errorObject: null,
      currentEditionId: "",
      currentEdition: null,
      canUserEdit: false,
      editionState: 0,
      mode: UniaGAVConstants.EditionEditModes.editDetails,
      versionEditMode: UniaGAVConstants.VersionEditModes.versionDetails,
      versionEditModes: UniaGAVConstants.VersionEditModes,
      editionEditModes: UniaGAVConstants.EditionEditModes,
      selectedLanguages: [],
    };
  },
  watch: {
    "$route.params.editionId"() {
      this.showEdition = false;
      setTimeout(() => {
        this.showEdition = true;
      }, 20);
    },
  },
  created: function () {},
  methods: {
    handleLangChange(selectedLanguages) {
      if (selectedLanguages) {
        this.selectedLanguages = Object.values(selectedLanguages);
      }
    },

    currentEditionLoaded(edition, latestEdition) {
      this.currentEditionId = edition.editionId;
      this.currentEdition = edition;
      this.canUserEdit =
        edition.ownerPrincipal == this.$GetCurrentUserName() &&
        edition.stateId == 1 &&
        latestEdition;

      setTimeout(() => {
        this.$store.commit(
          "setContractConfigurationCanUserEdit",
          this.canUserEdit,
        );
      }, 20);
      this.editionState = edition.stateId;
    },
    editionModeChanged(newMode) {
      this.mode = newMode;
    },
    versionModeChanged(newMode) {
      this.versionEditMode = newMode;
    },
  },
};
</script>

<style>
.contractDetailsForm .vdp-datepicker__calendar {
  margin-bottom: 30px;
}

.contractDetailsForm .vdp-datepicker__clear-button {
  position: absolute;
}
</style>

<style scoped>
.card-header-tab .card-header {
  background-color: #ffffff !important;
}

.card-title {
  margin-top: 10px;
  margin-bottom: 30px;
}

.form-group {
  font-size: initial;
}

.editionSection {
  border-top: 1px solid #ccc;
}
</style>
